import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { css } from '@emotion/react';
import { Box, Typography } from '@mui/material';
import { Types } from '@scorenco/core';

import { Game } from './_internal';

type TournamentGameItem = {
  id: number;
  date?: string;
  name?: string;
  place?: string;
  url?: string;
  status?: string;
  home_score?: number;
  home_score_data?: unknown;
  home_shootout?: number | null;
  away_score?: number;
  away_score_data?: unknown;
  away_shootout?: number | null;
  home_engagement?: {
    id: number;
    name?: string;
    team?: { id: number; name?: string; logo?: string } | null;
    phase?: {
      id: number;
      name?: string;
      rule?: {
        id?: number;
        challenge?: { id: number; name?: string } | null;
      } | null;
    } | null;
  } | null;
  away_engagement?: {
    id: number;
    name?: string;
    team?: { id: number; name?: string; logo?: string } | null;
  } | null;
};

type GetLiveMatchsData = {
  tournaments_game: TournamentGameItem[];
  tournaments_challenge_by_pk?: {
    id: number;
    name?: string;
    tournament?: { name?: string } | null;
  } | null;
};

type GetNotLiveMatchsData = {
  tournaments_game: TournamentGameItem[];
};

export const TournamentLives = ({
  screen,
}: {
  screen: Types.Clubhouse_Screen;
  clubHouse: Types.Clubhouse_Club_House;
}) => {
  const challengeId = screen.challenge_id;

  const getLiveMatchs = useQuery<GetLiveMatchsData>(
    gql`
      query GetLiveMatchs($challengeId: Int!) {
        tournaments_game(
          where: {
            status: { _eq: "live" }
            home_engagement: {
              phase: { rule: { challenge: { id: { _eq: $challengeId } } } }
            }
          }
          order_by: [{ date: asc }, { name: asc }]
        ) {
          id
          date
          name
          place
          url
          status
          home_score
          home_score_data
          home_shootout
          away_score
          away_score_data
          away_shootout
          home_engagement {
            id
            name
            team {
              id
              name
              logo
            }
            phase {
              id
              name
              rule {
                challenge {
                  id
                  name
                }
              }
            }
          }
          away_engagement {
            id
            name
            team {
              id
              name
              logo
            }
          }
        }
        tournaments_challenge_by_pk(id: $challengeId) {
          id
          name
          tournament {
            name
          }
        }
      }
    `,
    {
      variables: {
        challengeId: challengeId ?? 0,
      },
      skip: !challengeId,
      context: {
        role: 'club',
      },
      fetchPolicy: 'cache-and-network',
    }
  );

  const liveMatchs = getLiveMatchs.data?.tournaments_game ?? [];

  const isLive = !getLiveMatchs.data || liveMatchs.length !== 0;

  const getNotLiveMatchs = useQuery<GetNotLiveMatchsData>(
    gql`
      query GetNotLiveMatchs($challengeId: Int!) {
        tournaments_game(
          limit: 8
          where: {
            status: { _eq: "to_come" }
            home_engagement: {
              phase: { rule: { challenge: { id: { _eq: $challengeId } } } }
            }
          }
          order_by: [{ date: asc }, { name: asc }]
        ) {
          id
          date
          name
          place
          url
          status
          home_score
          home_score_data
          home_shootout
          away_score
          away_score_data
          away_shootout
          home_engagement {
            id
            name
            team {
              id
              name
              logo
            }
            phase {
              id
              name
              rule {
                id
                challenge {
                  id
                  name
                }
              }
            }
          }
          away_engagement {
            id
            name
            team {
              id
              name
              logo
            }
          }
        }
      }
    `,
    {
      variables: {
        challengeId: challengeId ?? 0,
      },
      skip: isLive,
      context: {
        role: 'club',
      },
    }
  );

  const notLiveMatchs = getNotLiveMatchs.data?.tournaments_game ?? [];

  const matchs = isLive ? liveMatchs : notLiveMatchs;

  // const matchs = getLiveMatchs.data?.tournaments_game.map((item) => ({
  //   ...item,
  // }));

  // const groupedMatchs = matchs?.reduce((acc, game) => {
  //   (acc[game.home_engagement.phase.rule.challenge.name] =
  //     acc[game.home_engagement.phase.rule.challenge.name] || []).push(game);

  //   return acc;
  // }, {});

  // const size = groupedMatchs
  //   ? Object.keys(groupedMatchs).length * 2.25 + matchs.length * 8
  //   : null;

  const style = {
    root: css`
      @keyframes scrollEvent {
        0% {
          transform: translateY(0);
        }
        15% {
          transform: translateY(0);
        }
        85% {
          transform: translateY(-${(matchs.length - 6) * 10.5}rem);
        }
        100% {
          transform: translateY(-${(matchs.length - 6) * 10.5}rem);
        }
      }
    `,
  };

  return (
    <Box css={style.root}>
      <Typography
        variant="h1"
        sx={{
          m: 0,
          textAlign: 'center',
          marginTop: '3rem',
        }}
      >
        {getLiveMatchs.data?.tournaments_challenge_by_pk?.name}
      </Typography>
      <Typography
        variant="h2"
        sx={{
          m: 0,
          textAlign: 'center',
        }}
      >
        {getLiveMatchs.data?.tournaments_challenge_by_pk?.tournament?.name}
      </Typography>

      {isLive ? (
        <Typography
          variant="h3"
          sx={{
            m: 0,
            textAlign: 'center',
            mb: 3,
          }}
        >
          🔴 Les matchs en cours
        </Typography>
      ) : (
        <Typography
          variant="h3"
          sx={{
            m: 0,
            textAlign: 'center',
            mb: 3,
          }}
        >
          Les matchs à venir
        </Typography>
      )}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          overflow: 'hidden',
        }}
      >
        {matchs.map((game, idx) => {
          const gameDate =
            game.status !== 'to_come' && game.date
              ? new Date(game.date).toLocaleDateString([], {
                  month: 'short',
                  day: '2-digit',
                })
              : null;

          return (
            <Box
              sx={{ marginBottom: '1rem', width: '50%' }}
              key={idx}
              css={css`
                animation: ${matchs.length > 6
                  ? `scrollEvent ${screen?.duration}s linear infinite`
                  : ''};
              `}
            >
              <Typography variant="h4">
                {game?.name} - {game?.home_engagement?.phase?.name}
              </Typography>
              <Box
                css={(theme) => css`
                  padding: 0.5rem 1rem;
                  border: 1px solid #e2e8ed;
                  color: ${theme.vars.palette.text.primary};
                  background-color: ${theme.vars.palette.background.paper};
                  transition:
                    box-shadow 0.5s cubic-bezier(0.165, 0.84, 0.44, 1),
                    background-color 0.5s cubic-bezier(0.165, 0.84, 0.44, 1);
                  min-height: 5rem;
                  box-sizing: content-box;
                `}
              >
                <Game game={{ game: game }} />
                <Box
                  css={css`
                    text-align: center;
                    font-size: 1.2rem;
                  `}
                >
                  {[game.place, gameDate].filter(Boolean).join(' - ')}
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
